import { Component, computed, inject, input, output } from '@angular/core';
import { Guest, TranslatablePair, User } from '@frontend/common';
import { Comment } from '@frontend/core';
import { PaginatedComments } from '../comments-paginated.model';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { PaginationModule } from '../../../ui/pagination/pagination.module';
import { CommentFormTypeTwoComponent } from '../comment-form-type-two/comment-form-type-two.component';
import { CommentTypeTwoComponent } from '../comment-type-two/comment-type-two.component';
import { PlaceholderService } from '@frontend/common';
import { generateLoremIpsum as createLoremIpsum } from '@frontend/common';
import { LockedContentMaskTypeTwoComponent } from '../../../ui';

@Component({
  selector: 'comments-post-display-pagination-all-in-one',
  templateUrl: './comments-post-display-pagination-all-in-one.component.html',
  styleUrls: ['./comments-post-display-pagination-all-in-one.component.scss'],
  imports: [
    TranslocoModule,
    PaginationModule,
    CommentFormTypeTwoComponent,
    CommentTypeTwoComponent,
    LockedContentMaskTypeTwoComponent,
  ],
  providers: [
    provideTranslocoScope('social'),
  ]
})
export class CommentsPostDisplayPaginationAllInOneComponent {

  user = input<User>();
  guest = input<Guest | undefined>(undefined);
  hasPermissionToReadComments = input<boolean>(false);
  hasPermissionToPostComments = input<boolean>(false);
  lockTitleTranslationKey = input<string | undefined>('authentication.login_required');
  lockDescriptionTranslationKey = input<string | undefined>('authentication.login_to_view');
  lockCtaTranslationKey = input<string | undefined>('common.go');
  rows = input<number | undefined>(undefined);
  loadingComments = input<boolean>(false);
  disabled = input<boolean>(false);
  clearCommentForm = input<boolean | undefined>(undefined); // if this changes, clear the comment form, no matter true or false
  formCachingKey = input<string | undefined>(undefined);
  postComment = output<string>();
  getComments = output<number>();
  paginatedComments =input<PaginatedComments | undefined>(undefined);
  titleText = input<string | undefined>(undefined);
  titleClasses = input<string>('h3 mb-3');
  titleTranslationKey = input<string | undefined>(undefined);
  titleTranslationScope = input<string | undefined>(undefined);
  descriptionText = input<string | undefined>(undefined);
  descriptionTranslationKey = input<string | undefined>(undefined);
  descriptionTranslationScope = input<string | undefined>(undefined);
  translatablePairs = input<TranslatablePair[]>([]); // Allow the user to translate FR->EN, for example
  placeholderService = inject(PlaceholderService);
  lockLinkClicked = output<void>();

  doGetComments(whichPage: number){
    this.getComments.emit(whichPage);
  }

  doPostComment (message: string){
    this.postComment.emit(message);
  }

  doLockLinkClicked (){
    this.lockLinkClicked.emit();
  }

  placeholderPaginatedComments = computed<PaginatedComments>(() => {

    const placeholderMessages = [
      createLoremIpsum(10, true),
      createLoremIpsum(10, true),
    ];
    const users = this.placeholderService.getUsers(2);
    const placeholderComments: Comment[] = users.map((user, index) => new Comment(
      placeholderMessages[index % placeholderMessages.length],
      index + 1,
      new Date(),
      user,
      user.avatar_disk,
      user.avatar_path,
      '',
    ));

    return {
      data: placeholderComments,
      links: {
        first: '',
        last: '',
        prev: '',
        next: '',
      },
      meta: {
        current_page: 1,
        from: 1,
        last_page: 1,
        path: '',
        per_page: Math.max(placeholderComments.length, 1),
        to: placeholderComments.length,
        total: placeholderComments.length,
      },
    };
  });

  displayPaginatedComments = computed<PaginatedComments | undefined>(() => {
    const hasPermission = this.hasPermissionToReadComments();
    const realPaginatedComments = this.paginatedComments();
    return hasPermission ? realPaginatedComments : this.placeholderPaginatedComments();
  });

}
